import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X,
  FileImage,
  FileCode,
  Monitor,
  Smartphone,
  Maximize,
  CheckCircle,
  AlertCircle,
  Loader2,
  Package,
  Building2,
  Search,
  Link2,
  FileText,
} from 'lucide-react';
import { useExportStore } from '../../../stores/exportStore';
import { useStudioStore } from '../../../stores/studioStore';
import { ExportProgressPanel } from './ExportProgressPanel';
import { BulkExportDialog } from './BulkExportDialog';
import { ReadingOrderModal } from './ReadingOrderModal';
import { isReadableElement } from '../../../lib/readingOrder';
import { generatePacketFilename, UNBRANDED_LEGACY_ID } from '../../../types/export';
import type { ExportFormat, JPEGQuality, ButtonUrlAssignment } from '../../../types/export';
import type { School, SchoolBrand, ButtonElement } from '../../../types/index';

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const FORMAT_OPTIONS: {
  value: ExportFormat;
  label: string;
  ext: string;
  desc: string;
}[] = [
  { value: 'jpeg', label: 'JPEG', ext: '.jpg', desc: 'Best for photos, smaller file size' },
  { value: 'png', label: 'PNG', ext: '.png', desc: 'Lossless quality, transparency support' },
  { value: 'webp', label: 'WebP', ext: '.webp', desc: 'Modern format, excellent compression' },
];

const QUALITY_OPTIONS: { value: JPEGQuality; label: string; desc: string }[] = [
  { value: 'low', label: 'Low', desc: '~60%' },
  { value: 'medium', label: 'Medium', desc: '~80%' },
  { value: 'high', label: 'High', desc: '~92%' },
  { value: 'maximum', label: 'Max', desc: '100%' },
];

const SCALE_OPTIONS = [
  { value: 1, label: '1x', desc: 'Standard', Icon: Monitor },
  { value: 2, label: '2x', desc: 'Retina', Icon: Smartphone },
  { value: 3, label: '3x', desc: 'Ultra', Icon: Maximize },
];

export function ExportDialog({ isOpen, onClose }: ExportDialogProps) {
  const { config, setConfig, currentJob, startExport, cancelExport, downloadBackground, downloadOverlay, downloadComposite, resetConfig, setButtonUrls, setLegacyId } =
    useExportStore();
  const { activeProject, canvasState } = useStudioStore();
  const schools = useStudioStore(s => s.schools);
  const schoolBrands = useStudioStore(s => s.schoolBrands);
  const schoolsLoading = useStudioStore(s => s.schoolsLoading);
  const editAsBrandId = useStudioStore(s => s.editAsBrandId);
  const loadSchools = useStudioStore(s => s.loadSchools);
  const loadSchoolBrands = useStudioStore(s => s.loadSchoolBrands);
  const dimensions = canvasState?.dimensions;

  const [showProgress, setShowProgress] = useState(false);
  const [showBulkExport, setShowBulkExport] = useState(false);
  const [showReadingOrder, setShowReadingOrder] = useState(false);

  const hasReadableElements = useMemo(
    () => (canvasState?.elements ?? []).some(isReadableElement),
    [canvasState?.elements]
  );
  const [exportMode, setExportMode] = useState<'template' | 'brand'>(editAsBrandId ? 'brand' : 'template');
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(editAsBrandId);
  const [brandSearch, setBrandSearch] = useState('');
  const [localButtonUrls, setLocalButtonUrls] = useState<ButtonUrlAssignment[]>([]);

  const buttonElements = useMemo(() => {
    if (!canvasState?.elements) return [];
    return canvasState.elements.filter(
      (el): el is ButtonElement => el.type === 'button' && el.layer === 'OVERLAY_LAYER'
    );
  }, [canvasState?.elements]);

  useEffect(() => {
    if (isOpen && buttonElements.length > 0) {
      setLocalButtonUrls(
        buttonElements.map((el) => ({
          elementId: el.id,
          elementName: el.name || el.text || 'Button',
          url: el.href || '',
        }))
      );
    }
  }, [isOpen, buttonElements]);

  const selectedSchool = useMemo(() => {
    if (!selectedBrandId) return null;
    const brand = schoolBrands.find(b => b.id === selectedBrandId);
    if (!brand) return null;
    return schools.find(s => s.id === brand.school_id) || null;
  }, [selectedBrandId, schoolBrands, schools]);

  const packetFilenamePreview = useMemo(() => {
    if (!activeProject?.name) return null;
    if (exportMode === 'brand') {
      if (!selectedSchool) return null;
      const effectiveId = selectedSchool.legacy_id || selectedSchool.short_name || selectedSchool.name;
      return generatePacketFilename(effectiveId, activeProject.name);
    }
    return generatePacketFilename(UNBRANDED_LEGACY_ID, activeProject.name);
  }, [exportMode, selectedSchool, activeProject?.name]);

  const isExporting =
    currentJob !== null &&
    currentJob.progress.status !== 'complete' &&
    currentJob.progress.status !== 'failed';

  useEffect(() => {
    if (isExporting) {
      setShowProgress(true);
    }
  }, [isExporting]);

  useEffect(() => {
    if (isOpen && exportMode === 'brand' && schools.length === 0 && !schoolsLoading) {
      loadSchools();
      loadSchoolBrands();
    }
  }, [isOpen, exportMode, schools.length, schoolsLoading, loadSchools, loadSchoolBrands]);

  useEffect(() => {
    if (isOpen) {
      setExportMode(editAsBrandId ? 'brand' : 'template');
      setSelectedBrandId(editAsBrandId);
      setBrandSearch('');
    }
  }, [isOpen, editAsBrandId]);

  const handleClose = useCallback(() => {
    if (!isExporting) {
      setShowProgress(false);
      resetConfig();
      onClose();
    }
  }, [isExporting, onClose, resetConfig]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isExporting) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, isExporting, handleClose]);

  const proceedWithExport = async () => {
    if (!activeProject) return;

    setButtonUrls(localButtonUrls);

    if (exportMode === 'brand' && selectedSchool) {
      setLegacyId(selectedSchool.legacy_id || selectedSchool.short_name || selectedSchool.name);
    } else {
      setLegacyId(UNBRANDED_LEGACY_ID);
    }

    setShowProgress(true);
    const brandIdToExport = exportMode === 'brand' ? selectedBrandId : null;
    await startExport(brandIdToExport);
  };

  const handleExport = () => {
    if (!activeProject) return;
    if (hasReadableElements) {
      setShowReadingOrder(true);
    } else {
      proceedWithExport();
    }
  };

  const updateButtonUrl = (elementId: string, url: string) => {
    setLocalButtonUrls(prev =>
      prev.map(b => (b.elementId === elementId ? { ...b, url } : b))
    );
  };

  if (!isOpen) return null;

  const scaledWidth = (dimensions?.width ?? 1200) * config.scale;
  const scaledHeight = (dimensions?.height ?? 400) * config.scale;
  const showQuality = config.format === 'jpeg' || config.format === 'webp';
  const canExport = (config.includeBackground || config.includeOverlay || config.includeComposite) &&
    (exportMode === 'template' || selectedBrandId !== null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={!isExporting ? handleClose : undefined}
      />

      <div className="relative z-10 max-w-lg w-full mx-4 bg-[#1a1a2e] border border-white/[0.1] rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <h2 className="text-lg font-semibold text-white font-['BioRhyme']">Export Asset</h2>
          {!isExporting && (
            <button
              onClick={handleClose}
              className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto sidebar-scroll">
          {showProgress ? (
            <ExportProgressPanel
              job={currentJob}
              onDownloadBackground={downloadBackground}
              onDownloadOverlay={downloadOverlay}
              onDownloadComposite={downloadComposite}
            />
          ) : (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white/60 mb-3">Export Mode</label>
                <div className="flex gap-1 p-1 rounded-lg bg-[#12121a]">
                  <button
                    onClick={() => { setExportMode('template'); setSelectedBrandId(null); }}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                      exportMode === 'template'
                        ? 'bg-cyan-500/20 text-cyan-400 shadow-sm'
                        : 'text-white/40 hover:text-white/60'
                    }`}
                  >
                    Template
                  </button>
                  <button
                    onClick={() => setExportMode('brand')}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                      exportMode === 'brand'
                        ? 'bg-cyan-500/20 text-cyan-400 shadow-sm'
                        : 'text-white/40 hover:text-white/60'
                    }`}
                  >
                    Brand Export
                  </button>
                </div>
              </div>

              {exportMode === 'brand' && (
                <SchoolSelector
                  schools={schools}
                  schoolBrands={schoolBrands}
                  schoolsLoading={schoolsLoading}
                  selectedBrandId={selectedBrandId}
                  onSelect={setSelectedBrandId}
                  search={brandSearch}
                  onSearchChange={setBrandSearch}
                />
              )}

              {packetFilenamePreview && (
                <div className="p-3 rounded-lg bg-[#0a0a0f] border border-cyan-500/10">
                  <div className="text-[10px] font-medium text-white/30 uppercase tracking-wider mb-1.5">
                    Packet Filename
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="text-cyan-400/60 flex-shrink-0" />
                    <code className="text-sm text-cyan-400 font-mono break-all">
                      {packetFilenamePreview}.jpeg / .txt
                    </code>
                  </div>
                  {exportMode === 'brand' && !selectedSchool?.legacy_id && (
                    <div className="mt-1.5 text-[10px] text-amber-400/60">
                      School has no Legacy ID -- filename will use a fallback identifier
                    </div>
                  )}
                  {exportMode === 'template' && (
                    <div className="mt-1.5 text-[10px] text-white/40">
                      Unbranded export -- uses "{UNBRANDED_LEGACY_ID}" as the ID prefix
                    </div>
                  )}
                </div>
              )}

              {buttonElements.length > 0 && config.includeOverlay && (
                <ButtonUrlSection
                  buttonUrls={localButtonUrls}
                  onUpdateUrl={updateButtonUrl}
                />
              )}

              <div>
                <label className="block text-sm font-medium text-white/60 mb-3">
                  Output Format
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {FORMAT_OPTIONS.map((opt) => {
                    const isActive = config.format === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setConfig({ format: opt.value })}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all ${
                          isActive
                            ? 'border-cyan-500 bg-cyan-500/10 text-white'
                            : 'border-white/[0.06] bg-white/[0.02] text-white/60 hover:bg-white/[0.06] hover:text-white'
                        }`}
                      >
                        <FileImage
                          size={20}
                          className={isActive ? 'text-cyan-400' : ''}
                        />
                        <span className="text-sm font-medium">{opt.label}</span>
                        <span className="text-[10px] text-white/40">{opt.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {showQuality && (
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-3">Quality</label>
                  <div className="flex gap-1 p-1 rounded-lg bg-[#12121a]">
                    {QUALITY_OPTIONS.map((opt) => {
                      const isActive = config.quality === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setConfig({ quality: opt.value })}
                          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                            isActive
                              ? 'bg-cyan-500/20 text-cyan-400 shadow-sm'
                              : 'text-white/40 hover:text-white/60'
                          }`}
                        >
                          <div>{opt.label}</div>
                          <div className="text-[10px] mt-0.5 opacity-60">{opt.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-white/60 mb-3">Scale</label>
                <div className="flex gap-2">
                  {SCALE_OPTIONS.map((opt) => {
                    const isActive = config.scale === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setConfig({ scale: opt.value })}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border transition-all ${
                          isActive
                            ? 'border-cyan-500 bg-cyan-500/10 text-white'
                            : 'border-white/[0.06] bg-white/[0.02] text-white/60 hover:bg-white/[0.06]'
                        }`}
                      >
                        <opt.Icon
                          size={16}
                          className={isActive ? 'text-cyan-400' : ''}
                        />
                        <div className="text-left">
                          <div className="text-sm font-medium">{opt.label}</div>
                          <div className="text-[10px] opacity-60">{opt.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/60 mb-3">Outputs</label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#12121a] border border-white/[0.06]">
                    <div className="flex items-center gap-3">
                      <FileImage size={18} className="text-white/40" />
                      <div>
                        <div className="text-sm text-white">Background Image</div>
                        <div className="text-xs text-white/40">Rasterized visual layer</div>
                      </div>
                      <span className="px-1.5 py-0.5 text-[10px] font-medium uppercase rounded bg-blue-500/10 text-blue-400">
                        {config.format}
                      </span>
                    </div>
                    <button
                      onClick={() => setConfig({ includeBackground: !config.includeBackground })}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        config.includeBackground ? 'bg-cyan-500' : 'bg-white/10'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          config.includeBackground ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#12121a] border border-white/[0.06]">
                    <div className="flex items-center gap-3">
                      <FileCode size={18} className="text-white/40" />
                      <div>
                        <div className="text-sm text-white">Overlay Packet</div>
                        <div className="text-xs text-white/40">JSON data for downstream Packet tool</div>
                      </div>
                      <span className="px-1.5 py-0.5 text-[10px] font-medium uppercase rounded bg-emerald-500/10 text-emerald-400">
                        .txt
                      </span>
                    </div>
                    <button
                      onClick={() => setConfig({ includeOverlay: !config.includeOverlay })}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        config.includeOverlay ? 'bg-cyan-500' : 'bg-white/10'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          config.includeOverlay ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#12121a] border border-white/[0.06]">
                    <div className="flex items-center gap-3">
                      <FileImage size={18} className="text-white/40" />
                      <div>
                        <div className="text-sm text-white">Composite Image</div>
                        <div className="text-xs text-white/40">Background + overlay merged into one image</div>
                      </div>
                      <span className="px-1.5 py-0.5 text-[10px] font-medium uppercase rounded bg-amber-500/10 text-amber-400">
                        {config.format}
                      </span>
                    </div>
                    <button
                      onClick={() => setConfig({ includeComposite: !config.includeComposite })}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        config.includeComposite ? 'bg-cyan-500' : 'bg-white/10'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          config.includeComposite ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-[#0a0a0f] border border-white/[0.06]">
                <div className="text-xs font-medium text-white/40 uppercase tracking-wider mb-2">
                  Export Summary
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-white/40">Mode</div>
                  <div className="text-white text-right">
                    {exportMode === 'brand' ? 'Brand Export' : 'Template'}
                  </div>
                  {exportMode === 'brand' && selectedBrandId && (
                    <>
                      <div className="text-white/40">School</div>
                      <div className="text-white text-right truncate">
                        {selectedSchool?.name || 'Selected'}
                      </div>
                    </>
                  )}
                  <div className="text-white/40">Dimensions</div>
                  <div className="text-white text-right">
                    {scaledWidth} x {scaledHeight}px
                  </div>
                  <div className="text-white/40">Format</div>
                  <div className="text-white text-right uppercase">{config.format}</div>
                  {showQuality && (
                    <>
                      <div className="text-white/40">Quality</div>
                      <div className="text-white text-right capitalize">{config.quality}</div>
                    </>
                  )}
                  <div className="text-white/40">Scale</div>
                  <div className="text-white text-right">{config.scale}x</div>
                  <div className="text-white/40">Files</div>
                  <div className="text-white text-right">
                    {[
                      config.includeBackground &&
                        `Background (.${config.format === 'jpeg' ? 'jpg' : config.format})`,
                      config.includeOverlay && 'Overlay (.txt)',
                      config.includeComposite &&
                        `Composite (.${config.format === 'jpeg' ? 'jpg' : config.format})`,
                    ]
                      .filter(Boolean)
                      .join(' + ') || 'None selected'}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowBulkExport(true)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-cyan-500/10 bg-cyan-500/[0.03] hover:bg-cyan-500/[0.06] transition-colors group"
              >
                <Package size={18} className="text-cyan-400/60 group-hover:text-cyan-400" />
                <div className="text-left flex-1">
                  <div className="text-sm text-white/70 group-hover:text-white">Bulk Export</div>
                  <div className="text-[10px] text-white/25">Export for multiple brands at once</div>
                </div>
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-white/[0.06]">
          {showProgress && currentJob?.progress.status === 'complete' ? (
            <>
              <div className="flex items-center gap-2 text-emerald-400 text-sm">
                <CheckCircle size={16} />
                Export complete
              </div>
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-lg bg-cyan-500 text-white text-sm font-medium hover:bg-cyan-600 transition-colors"
              >
                Done
              </button>
            </>
          ) : showProgress && currentJob?.progress.status === 'failed' ? (
            <>
              <div className="flex items-center gap-2 text-red-400 text-sm max-w-[60%] truncate">
                <AlertCircle size={16} className="flex-shrink-0" />
                <span className="truncate">
                  {currentJob.progress.error || 'Export failed'}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowProgress(false)}
                  className="px-4 py-2 rounded-lg text-white/60 text-sm hover:bg-white/[0.06] transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={proceedWithExport}
                  className="px-4 py-2 rounded-lg bg-cyan-500 text-white text-sm font-medium hover:bg-cyan-600 transition-colors"
                >
                  Retry
                </button>
              </div>
            </>
          ) : showProgress ? (
            <>
              <div className="flex items-center gap-2 text-white/40 text-sm">
                <Loader2 size={16} className="animate-spin" />
                Exporting...
              </div>
              <button
                onClick={cancelExport}
                className="px-4 py-2 rounded-lg text-red-400 text-sm hover:bg-red-500/10 transition-colors"
              >
                Abort
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-lg text-white/60 text-sm hover:bg-white/[0.06] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={!canExport}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                  canExport
                    ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                    : 'bg-white/5 text-white/20 cursor-not-allowed'
                }`}
              >
                {exportMode === 'brand' ? 'Export for Brand' : 'Export'}
              </button>
            </>
          )}
        </div>
      </div>

      <BulkExportDialog
        isOpen={showBulkExport}
        onClose={() => setShowBulkExport(false)}
      />

      <ReadingOrderModal
        isOpen={showReadingOrder}
        onClose={() => setShowReadingOrder(false)}
        onConfirm={() => {
          setShowReadingOrder(false);
          proceedWithExport();
        }}
      />
    </div>
  );
}

interface SchoolSelectorProps {
  schools: School[];
  schoolBrands: SchoolBrand[];
  schoolsLoading: boolean;
  selectedBrandId: string | null;
  onSelect: (brandId: string) => void;
  search: string;
  onSearchChange: (search: string) => void;
}

function SchoolSelector({
  schools,
  schoolBrands,
  schoolsLoading,
  selectedBrandId,
  onSelect,
  search,
  onSearchChange,
}: SchoolSelectorProps) {
  const schoolMap = new Map<string, { school: School; brands: SchoolBrand[] }>();
  for (const school of schools) {
    schoolMap.set(school.id, { school, brands: [] });
  }
  for (const brand of schoolBrands) {
    const entry = schoolMap.get(brand.school_id);
    if (entry) entry.brands.push(brand);
  }

  const q = search.toLowerCase();
  const filtered = Array.from(schoolMap.values()).filter(({ school, brands }) => {
    if (!q) return true;
    return (
      (school.name || '').toLowerCase().includes(q) ||
      (school.legacy_id || '').toLowerCase().includes(q) ||
      brands.some(
        (b) => (b.school_name || '').toLowerCase().includes(q)
      )
    );
  });

  return (
    <div>
      <label className="block text-sm font-medium text-white/60 mb-3">
        <span className="flex items-center gap-1.5">
          <Building2 size={14} />
          Select School / Brand
        </span>
      </label>
      <div className="rounded-lg border border-white/[0.06] bg-[#12121a] overflow-hidden">
        <div className="p-2 border-b border-white/[0.06]">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search schools, brands, or legacy ID..."
              className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50"
            />
            {search && (
              <button
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="max-h-48 overflow-y-auto sidebar-scroll">
          {schoolsLoading ? (
            <div className="p-4 text-center text-sm text-white/40">
              <Loader2 size={16} className="animate-spin inline mr-2" />
              Loading schools...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-center text-sm text-white/40">
              {search ? 'No matches found' : 'No schools available'}
            </div>
          ) : (
            filtered.map(({ school, brands }) => (
              <div key={school.id}>
                <div className="px-3 py-1.5 text-[10px] font-medium text-white/30 uppercase tracking-wider bg-white/[0.02] flex items-center gap-1.5">
                  <Building2 size={10} />
                  <span className="flex-1 truncate">{school.name}</span>
                  {school.legacy_id && (
                    <span className="text-white/20 font-mono">#{school.legacy_id}</span>
                  )}
                </div>
                {brands.length > 0 ? (
                  brands.map((brand) => (
                    <button
                      key={brand.id}
                      onClick={() => onSelect(brand.id)}
                      className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                        brand.id === selectedBrandId
                          ? 'bg-cyan-500/10 text-cyan-400'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <div className="flex gap-1">
                        {brand.rgb_primary && (
                          <div
                            className="w-3 h-3 rounded-full border border-white/20"
                            style={{ backgroundColor: brand.rgb_primary }}
                          />
                        )}
                        {brand.rgb_secondary && (
                          <div
                            className="w-3 h-3 rounded-full border border-white/20"
                            style={{ backgroundColor: brand.rgb_secondary }}
                          />
                        )}
                      </div>
                      <span className="truncate">
                        {brand.school_name || 'Default Brand'}
                      </span>
                      {brand.id === selectedBrandId && (
                        <CheckCircle size={12} className="ml-auto text-cyan-400 flex-shrink-0" />
                      )}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-xs text-white/30 italic">No brands configured</div>
                )}
              </div>
            ))
          )}
        </div>

        {!selectedBrandId && !schoolsLoading && (
          <div className="px-3 py-2 border-t border-white/[0.06] text-xs text-amber-400/60">
            Select a brand to export with brand-specific content
          </div>
        )}
      </div>
    </div>
  );
}

interface ButtonUrlSectionProps {
  buttonUrls: ButtonUrlAssignment[];
  onUpdateUrl: (elementId: string, url: string) => void;
}

function ButtonUrlSection({ buttonUrls, onUpdateUrl }: ButtonUrlSectionProps) {
  if (buttonUrls.length === 0) return null;

  return (
    <div>
      <label className="block text-sm font-medium text-white/60 mb-3">
        <span className="flex items-center gap-1.5">
          <Link2 size={14} />
          Button URLs
        </span>
      </label>
      <div className="space-y-2">
        {buttonUrls.map((btn) => (
          <div key={btn.elementId} className="rounded-lg bg-[#12121a] border border-white/[0.06] p-3">
            <div className="text-xs text-white/40 mb-1.5 truncate">{btn.elementName}</div>
            <input
              type="url"
              value={btn.url}
              onChange={(e) => onUpdateUrl(btn.elementId, e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50 font-mono"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
